'use client';

import { X } from '@phosphor-icons/react/ssr';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { IconButton } from './icon-button';
import { focusBodyFirst } from './overlay-focus';
import { modalPosition, modalStyles } from './overlay-styles';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type SectionProps = {
  /**
   * Figma "Size": Small 400 (confirmations, short forms) · Medium 560 (default) · Large 720.
   * Extra large 1120 × up to 880 tall (not in Figma) holds a side-by-side workspace.
   */
  size?: ModalSize;
  /** Figma "Title" - names the dialog. */
  title: ReactNode;
  /** Figma "Description" (Show description) - describes the dialog. */
  description?: ReactNode;
  /** Figma "Close button". On by default. */
  showClose?: boolean;
  /** Accessible name of the Close button. */
  closeLabel?: string;
  /**
   * Figma Footer actions, right-aligned: Secondary action (a Medium Ghost
   * "Cancel", usually `ModalClose asChild`) then one Primary. Omit for no footer.
   */
  footer?: ReactNode;
  /** Figma "Footer checkbox": the left of the footer, e.g. a "Don’t ask again" Checkbox. */
  footerStart?: ReactNode;
  /** Figma "Content": the body. Scrolls when the modal meets the viewport. */
  children?: ReactNode;
};

function ModalSections({
  size,
  title,
  description,
  showClose = true,
  closeLabel = 'Close',
  footer,
  footerStart,
  children,
}: SectionProps) {
  const styles = modalStyles({ size });
  return (
    <>
      <div data-slot="modal-header" className={styles.header()}>
        <div data-slot="modal-titles" className={styles.titles()}>
          <DialogTitle data-slot="modal-title" className={styles.title()}>
            {title}
          </DialogTitle>
          {description !== undefined && (
            <DialogDescription
              data-slot="modal-description"
              className={styles.description()}
            >
              {description}
            </DialogDescription>
          )}
        </div>
        {showClose && (
          <DialogClose asChild>
            <IconButton
              data-slot="modal-close"
              icon={X}
              size="sm"
              aria-label={closeLabel}
            />
          </DialogClose>
        )}
      </div>
      <div data-slot="modal-body" className={styles.body()}>
        {children}
      </div>
      {(footer !== undefined || footerStart !== undefined) && (
        <div data-slot="modal-footer" className={styles.footer()}>
          <div data-slot="modal-footer-start" className={styles.footerStart()}>
            {footerStart}
          </div>
          {footer}
        </div>
      )}
    </>
  );
}

export type ModalContentProps = Omit<ComponentProps<typeof DialogContent>, 'title'> &
  SectionProps;

/**
 * The Figma Modal: centred on the bg/overlay backdrop, focus trapped, Esc /
 * Close / a backdrop click dismiss it and focus returns to the trigger.
 * Initial focus goes to the body's first field or control, else Close.
 */
export function ModalContent({
  size,
  title,
  description,
  showClose,
  closeLabel,
  footer,
  footerStart,
  children,
  className,
  onOpenAutoFocus,
  ...props
}: ModalContentProps) {
  return (
    <DialogContent
      // No description: tell Radix not to expect one.
      {...(description === undefined ? { 'aria-describedby': undefined } : {})}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event);
        if (!event.defaultPrevented) focusBodyFirst(event, 'modal-body');
      }}
      className={cn(modalPosition, modalStyles({ size }).root(), className)}
      {...props}
    >
      <ModalSections
        size={size}
        title={title}
        description={description}
        showClose={showClose}
        closeLabel={closeLabel}
        footer={footer}
        footerStart={footerStart}
      >
        {children}
      </ModalSections>
    </DialogContent>
  );
}

export type ModalPanelProps = SectionProps & { className?: string };

/**
 * The modal drawn in place (docs, the Figma frame) - same classes as
 * `ModalContent`, without the portal, backdrop or focus trap.
 */
export function ModalPanel({ size, className, ...sections }: ModalPanelProps) {
  return (
    <DialogPrimitive.Root>
      <div
        data-slot="modal-content"
        className={modalStyles({ size }).root({ className })}
      >
        <ModalSections size={size} {...sections} />
      </div>
    </DialogPrimitive.Root>
  );
}

/**
 * Blocking dialog for focused tasks (Figma: Components ↳ Modal). Compose
 * `Modal` › `ModalTrigger asChild` › `ModalContent`. Keep modals rare: prefer
 * inline editing, a Popover or a Drawer when people need the page behind.
 * One Primary action; Cancel as `ModalClose asChild`.
 */
export function Modal(props: ComponentProps<typeof Dialog>) {
  return <Dialog {...props} />;
}

export { DialogClose as ModalClose, DialogTrigger as ModalTrigger };
